import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import Close32 from '@carbon/icons-react/es/close/32';
import { Button } from '@rocketlane/ui';
import { useMutation } from '@tanstack/react-query';
import { useBoolean } from 'ahooks';
import { Space } from 'antd';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountAPI, RequestPlanType } from 'api/account';
import { Form } from 'components/atoms/form';
import { LinkButton } from 'components/buttons';
import { InputNumber, Input } from 'components/facade/input';
import { Page } from 'components/molecules/page';
import { toastService } from 'components/molecules/toast';
import { ProductiveHeading01, ProductiveHeading03, Text01 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
const Icon = styled.h1`
  ${ProductiveHeading01};
`;
const Heading = styled.h3`
  ${ProductiveHeading03};
`;
const Message = styled.p``;
const StyledPage = styled(Page)`
  padding: var(--spacing-12);
`;
const Container = styled.div`
  width: 400px;
`;
const Text = styled.div``;
const SubmitButton = styled(Button)`
  width: 100%;
  ${Text} {
    flex-grow: 1;
    text-align: left;
  }
`;
const SecondaryButton = styled(LinkButton)`
  ${Text01};
  margin-top: var(--layout-01);
`;
const StyledCloseButton = styled(Button)`
  position: absolute;
  top: var(--spacing-10);
  right: var(--spacing-10);
`;
const useRequestPlanAccountMutation = (options)=>{
    return useMutation({
        mutationFn: (payload)=>AccountAPI.requestPlan(payload),
        ...options
    });
};
export const BuyPage = ({ isExpired, handleClose: closeForm })=>{
    const { user } = useUser();
    const requiredMessage = useTranslation('input.required');
    const errorTitle = useTranslation('account.trial.buy.error.title');
    const errorMessage = useTranslation('account.trial.buy.error.message');
    const shouldBeAtLeast1Message = useTranslation('routes.account_assessment.should_be_at_least_1');
    const [canShowSuccessMessage, { setTrue: showSuccessMessage }] = useBoolean(false);
    const { mutate, isLoading } = useRequestPlanAccountMutation({
        onSuccess: ()=>{
            showSuccessMessage();
        },
        onError: ()=>{
            toastService({
                title: errorTitle,
                message: errorMessage
            }).error();
        }
    });
    const handleClose = ()=>{
        window.location.reload();
    };
    return /*#__PURE__*/ _jsxs(StyledPage, {
        children: [
            canShowSuccessMessage || !isExpired ? /*#__PURE__*/ _jsx(StyledCloseButton, {
                leftIcon: /*#__PURE__*/ _jsx(Close32, {}),
                size: "large",
                variant: "ghost",
                type: "button",
                onClick: ()=>canShowSuccessMessage ? handleClose() : closeForm()
            }) : null,
            /*#__PURE__*/ _jsx(Container, {
                children: canShowSuccessMessage ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Icon, {
                            children: "🙌"
                        }),
                        /*#__PURE__*/ _jsx(Heading, {
                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: "account.trial.buy.thanks.heading",
                                values: {
                                    firstName: user.firstName || user.lastName
                                }
                            })
                        }),
                        /*#__PURE__*/ _jsxs(Space, {
                            direction: "vertical",
                            size: 32,
                            children: [
                                /*#__PURE__*/ _jsx(Message, {
                                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "account.trial.buy.thanks.message",
                                        values: {
                                            emailId: user.emailId,
                                            b: (chunks)=>/*#__PURE__*/ _jsx("b", {
                                                    children: chunks
                                                })
                                        }
                                    })
                                }),
                                !isExpired ? /*#__PURE__*/ _jsx(Button, {
                                    variant: "primary",
                                    type: "button",
                                    onClick: handleClose,
                                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "account.trial.buy.thanks.cta"
                                    })
                                }) : null
                            ]
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        !isExpired ? /*#__PURE__*/ _jsx(Icon, {
                            children: "🎉"
                        }) : null,
                        /*#__PURE__*/ _jsx(Heading, {
                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: isExpired ? 'account.trial.expired.buy.heading' : 'account.trial.buy.heading'
                            })
                        }),
                        /*#__PURE__*/ _jsxs(Form, {
                            layout: "vertical",
                            onFinish: (values)=>mutate({
                                    type: RequestPlanType.Buy,
                                    metaData: {
                                        plan: 'PREMIUM',
                                        ...values
                                    }
                                }),
                            initialValues: {
                                emailId: user.emailId
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Form.Item, {
                                    label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "account.trial.buy.form.your_email"
                                    }),
                                    name: "emailId",
                                    children: /*#__PURE__*/ _jsx(Input, {
                                        disabled: true,
                                        type: "email"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Form.Item, {
                                    label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "account.trial.buy.form.users"
                                    }),
                                    name: "noOfAgents",
                                    rules: [
                                        {
                                            min: 1,
                                            type: 'number',
                                            message: shouldBeAtLeast1Message
                                        },
                                        {
                                            required: true,
                                            message: requiredMessage
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsx(InputNumber, {
                                        min: 1,
                                        autoFocus: true
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SubmitButton, {
                                    variant: "primary",
                                    type: "submit",
                                    rightIcon: /*#__PURE__*/ _jsx(ArrowRight16, {}),
                                    disabled: isLoading,
                                    children: /*#__PURE__*/ _jsx(Text, {
                                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "submit"
                                        })
                                    })
                                })
                            ]
                        }),
                        isExpired ? /*#__PURE__*/ _jsx(SecondaryButton, {
                            className: "cp-w-full",
                            disabled: isLoading,
                            onClick: ()=>mutate({
                                    type: RequestPlanType.TrialExtend
                                }),
                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: "account.trial.buy.more_time",
                                values: {
                                    u: (chunks)=>/*#__PURE__*/ _jsx("u", {
                                            style: {
                                                padding: '0 var(--spacing-02)'
                                            },
                                            children: chunks
                                        })
                                }
                            })
                        }) : null
                    ]
                })
            })
        ]
    });
};
